import { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { Buildings } from "react-bootstrap-icons";
import { searchCompaniesByName, getCompanyByOrgNumber } from '../services/onboardingApiService';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { ERROR_CONTEXTS } from '../utils/errorMessages';
import { orgNummer, postalCodeValidate } from '../../util/validering/valideringsRutiner';
import { FormattedMessage } from 'react-intl';
import {
  OnboardingButtonLink,
  OnboardingButtonPrimary,
  OnboardingButtonOutlined,
} from "../components/OnboardingButton";
import { OnboardingInput } from "../components/OnboardingInput";
import {
  IngressDefault,
  LabelMedium,
  MediumLinkStrong,
} from "./OnboardingTypography";
import IconSearch from "../icons/IconSearch";
import {
  FormGrid,
  EmptyGridBlock,
  AddressContainer,
  AddressLineWithTwoValues,
  SearchContainer,
  DropdownContainer,
  DropdownItem,
  IconContainer,
  ContentContainer,
  MessageBox,
  THEME,
} from "./OnboardingLayoutComponents";

// Removed - now using imported components from OnboardingLayoutComponents

// Removed - now using imported components from OnboardingLayoutComponents

const CompanyInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CompanyName = styled.div`
  font-weight: 500;
  color: #333;
  font-size: 14px;
`;

const CompanyOrgNr = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 2px;
`;

const ScreenReaderOnly = styled.div`
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

const DropdownHeader = styled.div`
  padding: 12px 16px 0 16px;
  font-weight: bold;
  font-size: 14px;
  color: #333;
`;

const OnboardingGetCompanyInfo = ({
  orgNr,
  setOrgNr,
  companyName,
  setCompanyName,
  adress,
  setAdress,
  postnummer,
  setPostnummer,
  poststed,
  setPoststed,
  isEditing,
  setIsEditing,
  onValidationChange,
}) => {
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchContainerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Field validation errors
  const [errors, setErrors] = useState({
    companyName: '',
    orgNr: '',
    adress: '',
    postnummer: '',
    poststed: ''
  });

  // Use centralized error handling
  const errorHandler = useErrorHandler(ERROR_CONTEXTS.COMPANY_SEARCH);
  const inputRef = useRef(null);

  // Validation functions
  const validateField = (fieldName, value) => {
    let error = '';

    switch (fieldName) {
      case 'companyName':
        if (!value || value.trim() === '') {
          error = 'firmanavnIsRequired';
        }
        break;
      case 'orgNr':
        if (!value || value.trim() === '') {
          error = 'orgNrIsRequired';
        } else if (!orgNummer(value.replace(/\s/g, ''))) {
          error = 'isValidOrgNr';
        }
        break;
      case 'adress':
        if (!value || value.trim() === '') {
          error = 'adresseIsRequired';
        }
        break;
      case 'postnummer':
        if (!value || value.trim() === '') {
          error = 'postnrIsRequired';
        } else if (!postalCodeValidate(value)) {
          error = 'postnrInvalid';
        }
        break;
      case 'poststed':
        if (!value || value.trim() === '') {
          error = 'poststedIsRequired';
        }
        break;
      default:
        break;
    }

    setErrors(prev => ({ ...prev, [fieldName]: error }));
    return error === '';
  };

  const validateAllFields = () => {
    const isCompanyNameValid = validateField('companyName', companyName);
    const isOrgNrValid = validateField('orgNr', orgNr);
    const isAdressValid = validateField('adress', adress);
    const isPostnummerValid = validateField('postnummer', postnummer);
    const isPoststedValid = validateField('poststed', poststed);

    return isCompanyNameValid && isOrgNrValid && isAdressValid && isPostnummerValid && isPoststedValid;
  };

  const hasValidationErrors = () => {
    return Object.values(errors).some(error => error !== '');
  };

  // Notify parent of validation state changes
  useEffect(() => {
    if (onValidationChange) {
      const hasErrors = hasValidationErrors();
      const allFieldsFilled = companyName && orgNr && adress && postnummer && poststed;
      const isValid = !hasErrors && allFieldsFilled;
      onValidationChange(isValid);
    }
  }, [errors, companyName, orgNr, adress, postnummer, poststed, onValidationChange]);

  const handleSave = () => {
    if (validateAllFields()) {
      setIsEditing(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const isOrgNumber = (value) => {
    return /^\d{9}$/.test(value.replace(/\s/g, ''));
  };

  const searchByName = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setIsSearching(true);
    errorHandler.clearError();
    
    try {
      const companies = await searchCompaniesByName(searchTerm);
      setSearchResults(companies);
      setShowDropdown(companies.length > 0);
      setHighlightedIndex(-1);
    } catch (error) {
      errorHandler.handleError(error, ERROR_CONTEXTS.COMPANY_SEARCH);
      setSearchResults([]);
      setShowDropdown(false);
    } finally {
      setIsSearching(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setOrgNr(value);

    // Clear error when user starts typing
    if (errors.orgNr) {
      setErrors(prev => ({ ...prev, orgNr: '' }));
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (!isOrgNumber(value)) {
      debounceTimerRef.current = setTimeout(() => {
        searchByName(value);
      }, 300);
    } else {
      setSearchResults([]);
      setShowDropdown(false);
      setHighlightedIndex(-1);
    }
  };

  const handleCompanySelect = useCallback((company) => {
    setOrgNr(company.organisasjonsnummer);
    setCompanyName(company.navn);
    const addressValue = company.forretningsadresse?.adresse;
    setAdress(Array.isArray(addressValue) ? addressValue.join(", ") : (addressValue || ""));
    setPostnummer(company.forretningsadresse?.postnummer || "");
    setPoststed(company.forretningsadresse?.poststed || "");
    setShowDropdown(false);
    setSearchResults([]);
    setHighlightedIndex(-1);

    // Validate data from API
    setTimeout(() => {
      validateField('companyName', company.navn);
      validateField('orgNr', company.organisasjonsnummer);
      const addr = Array.isArray(addressValue) ? addressValue.join(", ") : (addressValue || "");
      validateField('adress', addr);
      validateField('postnummer', company.forretningsadresse?.postnummer || "");
      validateField('poststed', company.forretningsadresse?.poststed || "");
    }, 0);
  }, [setOrgNr, setCompanyName, setAdress, setPostnummer, setPoststed, setShowDropdown, setSearchResults, setHighlightedIndex]);

  const handleKeyDown = useCallback((e) => {
    if (!showDropdown || searchResults.length === 0) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < searchResults.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : searchResults.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < searchResults.length) {
          handleCompanySelect(searchResults[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowDropdown(false);
        setHighlightedIndex(-1);
        inputRef.current?.focus();
        break;
      case 'Tab':
        setShowDropdown(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  }, [showDropdown, searchResults, highlightedIndex, handleCompanySelect]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowDropdown(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.addEventListener('keydown', handleKeyDown);
      return () => {
        if (inputRef.current) {
          inputRef.current.removeEventListener('keydown', handleKeyDown);
        }
      };
    }
  }, [handleKeyDown]);

  const handleButtonClick = async () => {
    if (!orgNr) {
      console.error("Input is empty");
      return;
    }

    errorHandler.clearError();

    if (isOrgNumber(orgNr)) {
      try {
        const company = await getCompanyByOrgNumber(orgNr);

        if (company) {
          setCompanyName(company.navn);
          const addressValue = company.forretningsadresse?.adresse;
          const addr = Array.isArray(addressValue) ? addressValue.join(", ") : (addressValue || "");
          setAdress(addr);
          const postnr = company.forretningsadresse?.postnummer || "";
          const postst = company.forretningsadresse?.poststed || "";
          setPostnummer(postnr);
          setPoststed(postst);

          // Validate data from API
          setTimeout(() => {
            validateField('companyName', company.navn);
            validateField('orgNr', orgNr);
            validateField('adress', addr);
            validateField('postnummer', postnr);
            validateField('poststed', postst);
          }, 0);
        }
      } catch (error) {
        errorHandler.handleError(error, ERROR_CONTEXTS.COMPANY_SEARCH);
      }
    } else {
      await searchByName(orgNr);
    }
  };

  return (
    <div>
      {/* Screen reader announcement region */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
      >
        {Object.values(errors).filter(e => e).length > 0 && (
          <FormattedMessage
            id="validationErrorsDetected"
            defaultMessage="Skjemaet har valideringsfeil som må rettes"
          />
        )}
      </div>

      {errorHandler.hasError && (
        <MessageBox {...errorHandler.getMessageBoxProps()} />
      )}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: THEME.spacing.lg,
          alignItems: "flex-end",
        }}
      >
        <SearchContainer ref={searchContainerRef} style={{ flex: "1 1 300px", minWidth: "250px" }}>
          <OnboardingInput
            ref={inputRef}
            label="Ditt firma"
            placeholder="Søk på navn eller organisasjonsnummer"
            value={orgNr}
            onChange={handleInputChange}
            onBlur={() => validateField('orgNr', orgNr)}
            error={errors.orgNr ? <FormattedMessage id={errors.orgNr} /> : ''}
            role="combobox"
            aria-expanded={showDropdown}
            aria-haspopup="listbox"
            aria-autocomplete="list"
            aria-activedescendant={highlightedIndex >= 0 ? `company-option-${highlightedIndex}` : undefined}
            aria-describedby="search-instructions"
          />
          {showDropdown && searchResults.length > 0 && (
            <DropdownContainer role="listbox" aria-label="Søkeresultater for firmaer">
              <DropdownHeader>Firma</DropdownHeader>
              {searchResults.map((company, index) => (
                <DropdownItem
                  key={company.organisasjonsnummer}
                  id={`company-option-${index}`}
                  role="option"
                  aria-selected={highlightedIndex === index}
                  className={highlightedIndex === index ? 'highlighted' : ''}
                  onMouseDown={(e) => {
                    e.preventDefault(); // Prevent input blur
                    handleCompanySelect(company);
                  }}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <IconContainer size="24px" aria-hidden="true">
                    <Buildings size={14} />
                  </IconContainer>
                  <CompanyInfo>
                    <CompanyName>{company.navn}</CompanyName>
                  </CompanyInfo>
                </DropdownItem>
              ))}
            </DropdownContainer>
          )}
          <ScreenReaderOnly id="search-instructions">
            Bruk piltastene for å navigere, Enter for å velge, Escape for å lukke
          </ScreenReaderOnly>
        </SearchContainer>
        <div style={{ flex: "0 0 auto" }}>
          <OnboardingButtonPrimary type="button" onClick={handleButtonClick}>
            <div style={{ display: "flex", gap: THEME.spacing.sm }}>
              <LabelMedium>Hent firmainfo</LabelMedium>
              <IconSearch width={24} height={24} fill="#000" />
            </div>
          </OnboardingButtonPrimary>
        </div>
      </div>
      <div>
        {adress &&
          (isEditing ? (
            <div>
              <FormGrid columnGap="60px" rowGap="32px">
                <OnboardingInput
                  label="Navn"
                  value={companyName}
                  placeholder={companyName}
                  onChange={(e) => {
                    setCompanyName(e.target.value);
                    if (errors.companyName) setErrors(prev => ({ ...prev, companyName: '' }));
                  }}
                  onBlur={() => validateField('companyName', companyName)}
                  error={errors.companyName ? <FormattedMessage id={errors.companyName} /> : ''}
                  required
                />
                <EmptyGridBlock />

                <OnboardingInput
                  label="Adresse"
                  value={adress}
                  placeholder={adress}
                  onChange={(e) => {
                    setAdress(e.target.value);
                    if (errors.adress) setErrors(prev => ({ ...prev, adress: '' }));
                  }}
                  onBlur={() => validateField('adress', adress)}
                  error={errors.adress ? <FormattedMessage id={errors.adress} /> : ''}
                  required
                />
                <EmptyGridBlock />

                <OnboardingInput
                  label="Postnummer"
                  value={postnummer}
                  placeholder={postnummer}
                  onChange={(e) => {
                    setPostnummer(e.target.value);
                    if (errors.postnummer) setErrors(prev => ({ ...prev, postnummer: '' }));
                  }}
                  onBlur={() => validateField('postnummer', postnummer)}
                  error={errors.postnummer ? <FormattedMessage id={errors.postnummer} /> : ''}
                  required
                />
                <OnboardingInput
                  label="Poststed"
                  value={poststed}
                  placeholder={poststed}
                  onChange={(e) => {
                    setPoststed(e.target.value);
                    if (errors.poststed) setErrors(prev => ({ ...prev, poststed: '' }));
                  }}
                  onBlur={() => validateField('poststed', poststed)}
                  error={errors.poststed ? <FormattedMessage id={errors.poststed} /> : ''}
                  required
                />
              </FormGrid>
              <OnboardingButtonOutlined onClick={handleSave}>
                <LabelMedium>Lagre</LabelMedium>
              </OnboardingButtonOutlined>
            </div>
          ) : (
            <div>
              <AddressContainer>
                <AddressLineWithTwoValues>
                  <LabelMedium>{companyName}</LabelMedium>
                  <IngressDefault>(org. nr.: {orgNr})</IngressDefault>
                </AddressLineWithTwoValues>

                <LabelMedium>{adress}</LabelMedium>

                <LabelMedium>{`${postnummer}  ${poststed}`}</LabelMedium>
              </AddressContainer>

              <OnboardingButtonLink onClick={handleEdit}>
                <MediumLinkStrong>Endre adresse</MediumLinkStrong>
              </OnboardingButtonLink>
            </div>
          ))}
      </div>
    </div>
  );
};

export default OnboardingGetCompanyInfo;
